import { useState, type FormEvent } from 'react';
import { Check, ExternalLink, Plus, ShoppingCart, Undo2 } from 'lucide-react';
import { ConfirmDeleteButton } from '../components/confirm-delete-button';
import { toast } from 'sonner';
import {
  useCreateShopping,
  useDeleteShopping,
  useShopping,
  useToggleShoppingBought,
  type ShoppingTag,
} from '../lib/hooks';

type TagDef = {
  value: ShoppingTag;
  label: string;
  emoji: string;
  className: string;
};

const TAGS: TagDef[] = [
  { value: 'amazon', label: 'Amazon', emoji: '📦', className: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-400/30 dark:bg-amber-500/10 dark:text-amber-200' },
  { value: 'private', label: 'Cá nhân', emoji: '🏠', className: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-400/30 dark:bg-emerald-500/10 dark:text-emerald-200' },
  { value: 'thaihoang', label: 'Thai Hoàng', emoji: '🏢', className: 'border-rose-300 bg-rose-50 text-rose-900 dark:border-rose-400/30 dark:bg-rose-500/10 dark:text-rose-200' },
  { value: 'other', label: 'Khác', emoji: '📌', className: 'border-slate-300 bg-slate-50 text-slate-700 dark:border-white/10 dark:bg-white/5 dark:text-slate-300' },
];

export function ShoppingPage() {
  const [activeTag, setActiveTag] = useState<ShoppingTag | 'all'>('all');
  const filters = activeTag === 'all' ? undefined : { tag: activeTag };
  const items = useShopping(filters);
  const create = useCreateShopping();
  const toggle = useToggleShoppingBought();
  const del = useDeleteShopping();

  const [newItem, setNewItem] = useState('');
  const [newTag, setNewTag] = useState<ShoppingTag>('private');
  const [newQty, setNewQty] = useState(1);

  const onAdd = async (e: FormEvent) => {
    e.preventDefault();
    if (!newItem.trim()) return;
    await create.mutateAsync({ item: newItem.trim(), tag: newTag, qty: newQty });
    setNewItem('');
    setNewQty(1);
    toast.success('Đã thêm vào shopping list');
  };

  const pending = items.data?.filter((i) => i.status === 'pending') ?? [];
  const bought = items.data?.filter((i) => i.status === 'bought') ?? [];
  const counts = (tag: ShoppingTag) => items.data?.filter((i) => i.tag === tag).length ?? 0;
  const tagDef = (tag: ShoppingTag) => TAGS.find((t) => t.value === tag) ?? TAGS[3]!;

  return (
    <div className="mx-auto max-w-5xl space-y-6 p-6 lg:p-8">
      <header>
        <h1 className="flex items-center gap-2 text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          <ShoppingCart className="h-7 w-7 text-fuchsia-500 dark:text-fuchsia-300" />
          Shopping
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          {pending.length} cần mua · {bought.length} đã mua · phân loại theo Amazon / Cá nhân / Thai Hoàng
        </p>
      </header>

      {/* Quick add */}
      <form onSubmit={onAdd} className="panel">
        <div className="panel-body space-y-3">
          <div className="flex gap-2">
            <input
              type="text"
              className="input flex-1"
              value={newItem}
              onChange={(e) => setNewItem(e.target.value)}
              placeholder="VD: 5kg gạo Jasmine, hoặc đặt sách trên Amazon..."
              autoFocus
            />
            <input
              type="number"
              min="1"
              max="9999"
              className="input w-20"
              value={newQty}
              onChange={(e) => setNewQty(Number(e.target.value))}
              placeholder="qty"
            />
            <button type="submit" className="btn-primary" disabled={create.isPending || !newItem.trim()}>
              <Plus className="h-4 w-4" />
              Thêm
            </button>
          </div>
          <div className="flex flex-wrap gap-2">
            {TAGS.map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => setNewTag(t.value)}
                className={`rounded-full border px-3 py-1.5 text-xs font-medium transition ${
                  newTag === t.value
                    ? t.className + ' ring-2 ring-offset-2 ring-indigo-400 ring-offset-white dark:ring-offset-slate-900'
                    : 'border-slate-300 bg-white text-slate-600 hover:border-slate-400 dark:border-white/10 dark:bg-white/5 dark:text-slate-400 dark:hover:border-white/20'
                }`}
              >
                <span>{t.emoji}</span> <span className="ml-1">{t.label}</span>
              </button>
            ))}
          </div>
        </div>
      </form>

      {/* Tag filter chips */}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => setActiveTag('all')}
          className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
            activeTag === 'all'
              ? 'border-indigo-300 bg-indigo-100 text-indigo-900 dark:border-indigo-400/30 dark:bg-indigo-500/15 dark:text-indigo-200'
              : 'border-slate-300 bg-white text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400'
          }`}
        >
          Tất cả ({items.data?.length ?? 0})
        </button>
        {TAGS.map((t) => (
          <button
            key={t.value}
            type="button"
            onClick={() => setActiveTag(t.value)}
            className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
              activeTag === t.value
                ? t.className
                : 'border-slate-300 bg-white text-slate-600 dark:border-white/10 dark:bg-white/5 dark:text-slate-400'
            }`}
          >
            <span>{t.emoji}</span> <span className="ml-1">{t.label}</span> ({counts(t.value)})
          </button>
        ))}
      </div>

      {/* Pending list */}
      <section className="panel">
        <div className="panel-header">
          <h2 className="panel-title">Cần mua ({pending.length})</h2>
        </div>
        {pending.length === 0 ? (
          <div className="panel-body py-10 text-center text-sm text-slate-500">
            🎉 Không còn gì cần mua
          </div>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-white/5">
            {pending.map((it) => {
              const td = tagDef(it.tag);
              return (
                <li key={it.id} className="group flex items-center gap-3 px-6 py-3 hover:bg-slate-50/60 dark:hover:bg-white/[0.02]">
                  <button
                    type="button"
                    onClick={() => toggle.mutate({ id: it.id, bought: true })}
                    className="rounded-lg border border-slate-300 p-1 text-slate-400 transition hover:border-emerald-400 hover:text-emerald-500 dark:border-white/15"
                    title="Đánh dấu đã mua"
                  >
                    <Check className="h-4 w-4" />
                  </button>
                  <span
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${td.className}`}
                  >
                    <span>{td.emoji}</span> {td.label}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                      {it.qty > 1 ? `${it.qty}× ` : ''}{it.item}
                    </div>
                    {it.notes && (
                      <div className="mt-0.5 truncate text-[11px] text-slate-500">{it.notes}</div>
                    )}
                  </div>
                  {it.url && (
                    <a
                      href={it.url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-icon opacity-60 hover:opacity-100"
                      title="Mở link"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                  {it.priceEur && (
                    <span className="text-xs text-slate-500">€{it.priceEur}</span>
                  )}
                  <div className="opacity-100 md:opacity-60 md:group-hover:opacity-100 transition">
                    <ConfirmDeleteButton
                      size="md"
                      label={`Xoá "${it.item}"`}
                      disabled={del.isPending}
                      onConfirm={() => {
                        del.mutate(it.id, {
                          onSuccess: () => toast.success('Đã xoá'),
                          onError: (e) =>
                            toast.error(e instanceof Error ? e.message : 'Không xoá được'),
                        });
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {/* Bought (collapsed by default — show last 10) */}
      {bought.length > 0 && (
        <section className="panel">
          <div className="panel-header">
            <h2 className="panel-title">Đã mua ({bought.length})</h2>
          </div>
          <ul className="divide-y divide-slate-200 dark:divide-white/5">
            {bought.slice(0, 10).map((it) => {
              const td = tagDef(it.tag);
              return (
                <li
                  key={it.id}
                  className="flex items-center gap-3 px-6 py-2.5 text-sm text-slate-500 line-through opacity-70"
                >
                  <button
                    type="button"
                    onClick={() => toggle.mutate({ id: it.id, bought: false })}
                    className="btn-icon"
                    title="Bỏ đánh dấu"
                  >
                    <Undo2 className="h-4 w-4" />
                  </button>
                  <span className="text-xs">{td.emoji}</span>
                  <span className="flex-1">
                    {it.qty > 1 ? `${it.qty}× ` : ''}{it.item}
                  </span>
                  {it.boughtAt && (
                    <span className="text-[11px] text-slate-400">
                      {new Date(it.boughtAt).toLocaleDateString('vi')}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </section>
      )}
    </div>
  );
}
